"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ShoppingBag,
  Users,
  Cake,
  DollarSign,
  Calendar,
  TrendingUp,
  Lock,
  FileText,
  AlertCircle,
  ArrowRight,
  Clock,
  Package,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useAppManager } from "@/context/AppManagerContext";
import { UserRoles } from "@/interfaces";
import { PlanFeatureNames, type PlanFeatures } from "@/interfaces/plan";
import { useNavigate } from "react-router-dom";
import { numberToMoney } from "@/util/moneyConversion";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import type { HomeOutput } from "@/interfaces/home";
import { HomeService } from "@/services/HomeService";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AppManager } from "@/services/AppManager";

interface UserPermissions {
  canViewFinancials: boolean;
  canViewReports: boolean;
  canViewCalendar: boolean;
}

// Dados de fallback
const FALLBACK_DATA = {
  monthlyRevenue: 15230,
  eventsToday: 7,
  reports: {},
};

const hasPlanPermissions = (planFeatures: PlanFeatures[]): UserPermissions => {
  return {
    canViewFinancials: planFeatures.some(
      (feature) =>
        feature.name === PlanFeatureNames.DISPLAY_DASHBOARD &&
        feature.value === "true"
    ),
    canViewReports: planFeatures.some(
      (feature) =>
        feature.name === PlanFeatureNames.DISPLAY_DELINQUENCY &&
        feature.value === "true"
    ),
    canViewCalendar: planFeatures.some(
      (feature) =>
        feature.name === PlanFeatureNames.DISPLAY_CALENDAR &&
        feature.value === "true"
    ),
  };
};

const hasUserRolesPermissions = (userRole: UserRoles): UserPermissions => {
  return {
    canViewFinancials: [UserRoles.ADMIN, UserRoles.OWNER].includes(userRole),
    canViewReports: [UserRoles.ADMIN, UserRoles.OWNER].includes(userRole),
    canViewCalendar: true,
  };
};

export default function Home() {
  const { currentPlan, currentRole } = useAppManager();

  const [home, setHome] = useState<HomeOutput>();
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  // Calcular as permissões do usuário
  const planPermissions = hasPlanPermissions(currentPlan?.features || []);
  const rolePermissions = hasUserRolesPermissions(currentRole);

  const getAccessStatus = (feature: keyof UserPermissions) => {
    const hasPlanAccess = planPermissions[feature];
    const hasRoleAccess = rolePermissions[feature];

    if (!hasPlanAccess) {
      return {
        isAccessible: false,
        restrictionReason: "Acesso restrito ao plano atual",
      };
    }

    if (!hasRoleAccess) {
      return {
        isAccessible: false,
        restrictionReason: "Acesso restrito à função atual",
      };
    }

    return {
      isAccessible: true,
      restrictionReason: "",
    };
  };

  // Verificar acesso para cada recurso
  const financialAccess = getAccessStatus("canViewFinancials").isAccessible;
  const calendarAccess = getAccessStatus("canViewCalendar").isAccessible;
  const reportsAccess = getAccessStatus("canViewReports").isAccessible;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const homeService = new HomeService();

        const homeResponse = await homeService.findOne();

        setHome({
          ...homeResponse,
          ordersToday: financialAccess
            ? homeResponse.ordersToday
            : FALLBACK_DATA.eventsToday,
          totalAmount: financialAccess
            ? homeResponse.totalAmount
            : FALLBACK_DATA.monthlyRevenue,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [financialAccess, calendarAccess, reportsAccess]);

  if (loading) {
    return <LoadingSpinner />;
  }

  const shortcuts = [
    {
      icon: <ShoppingBag />,
      label: "Novo Pedido",
      route: "/pedidos/novo",
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: <Users />,
      label: "Novo Cliente",
      route: "/clientes/novo",
      color: "from-green-500 to-green-600",
    },
    {
      icon: <Cake />,
      label: "Novo Produto",
      route: "/produtos/novo",
      color: "from-purple-500 to-purple-600",
    },
    {
      icon: <FileText />,
      label: "Nova Despesa",
      route: "/despesas/novo",
      color: "from-red-500 to-red-600",
    },
    {
      icon: <Package />,
      label: "Pedido Rápido",
      route: "/pedidos-rapidos/novo",
      color: "from-[#EE7B11] to-[#f59642]",
    },
  ];

  const currentDate = new Date();
  const formattedDate = format(currentDate, "EEEE, dd 'de' MMMM 'de' yyyy", {
    locale: ptBR,
  });

  return (
    <div className="min-h-screen p-6 md:p-8 bg-gradient-to-br from-[#f8f9fa] to-[#EE7B11]/5">
      {/* Cabeçalho com saudação e data */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-800">
          Olá,{" "}
          {AppManager.getInstance().getUser()?.name?.split(" ")[0] ||
            "Bem-vindo"}
          !
        </h1>
        <p className="text-gray-500 mt-1 capitalize">{formattedDate}</p>
      </motion.div>

      {/* Seção de Ações Rápidas */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-8"
      >
        <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
          <Clock className="mr-2 h-5 w-5 text-[#EE7B11]" />
          Ações Rápidas
        </h2>
        <div className="flex flex-wrap gap-3">
          {shortcuts.map((shortcut, index) => (
            <motion.div
              key={shortcut.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 * index }}
            >
              <Button
                className={`bg-gradient-to-r ${shortcut.color} text-white hover:shadow-lg transition-all duration-300 rounded-lg px-4 py-2 flex items-center space-x-2`}
                onClick={() => navigate(shortcut.route)}
              >
                {React.cloneElement(shortcut.icon, { className: "w-4 h-4" })}
                <span>{shortcut.label}</span>
              </Button>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Grade de Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <InfoCard
            icon={<ShoppingBag className="text-[#EE7B11]" />}
            title="Pedidos Recentes"
            description="Últimos pedidos registrados"
          >
            <ul className="space-y-3">
              {home?.recentOrders?.slice(0, 4).map((order, index) => (
                <motion.li
                  key={order.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 * index }}
                  className="text-gray-800 flex justify-between items-center border-b pb-2 border-gray-100"
                >
                  <span className="font-medium truncate max-w-[150px]">
                    {order.customerName ||
                      `Pedido #${order.id.substring(0, 8)}`}
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-200"
                  >
                    {numberToMoney(order.totalAmount)}
                  </Badge>
                </motion.li>
              ))}
            </ul>
            <Button
              variant="ghost"
              className="w-full mt-3 text-[#EE7B11] hover:text-[#d06a0a] hover:bg-[#EE7B11]/10"
              onClick={() => navigate("/pedidos")}
            >
              Ver todos os pedidos
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </InfoCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <InfoCard
            icon={<Users className="text-[#EE7B11]" />}
            title="Clientes"
            description="Visão geral dos clientes"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-800">
                  {home?.customersCount}
                </p>
                <p className="text-sm text-gray-500">Total de clientes</p>
              </div>
              <div className="bg-[#EE7B11]/10 p-3 rounded-full">
                <Users className="h-8 w-8 text-[#EE7B11]" />
              </div>
            </div>
            <div className="mt-4">
              <Button
                variant="ghost"
                className="w-full text-[#EE7B11] hover:text-[#d06a0a] hover:bg-[#EE7B11]/10"
                onClick={() => navigate("/clientes")}
              >
                Gerenciar clientes
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </InfoCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <InfoCard
            icon={<Cake className="text-[#EE7B11]" />}
            title="Produtos"
            description="Catálogo de produtos"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-800">
                  {home?.productsCount}
                </p>
                <p className="text-sm text-gray-500">Total de produtos</p>
              </div>
              <div className="bg-[#EE7B11]/10 p-3 rounded-full">
                <Cake className="h-8 w-8 text-[#EE7B11]" />
              </div>
            </div>
            <div className="mt-4">
              <Button
                variant="ghost"
                className="w-full text-[#EE7B11] hover:text-[#d06a0a] hover:bg-[#EE7B11]/10"
                onClick={() => navigate("/produtos")}
              >
                Gerenciar produtos
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </InfoCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <RestrictedCard
            icon={<DollarSign className="text-[#EE7B11]" />}
            title="Financeiro"
            description="Resumo financeiro"
            {...getAccessStatus("canViewFinancials")}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-800">
                  {numberToMoney(home?.totalAmount)}
                </p>
                <p className="text-sm text-gray-500">Faturamento do mês</p>
              </div>
              <div className="bg-green-50 p-3 rounded-full">
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </RestrictedCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <RestrictedCard
            icon={<Calendar className="text-[#EE7B11]" />}
            title="Calendário"
            description="Próximos eventos e entregas"
            {...getAccessStatus("canViewCalendar")}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-800">
                  {home?.ordersToday}
                </p>
                <p className="text-sm text-gray-500">Eventos hoje</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-full">
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <Button
                className="w-full bg-[#EE7B11] text-white hover:bg-[#d06a0a]"
                onClick={() => navigate("/entregas")}
              >
                Ver Calendário
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </RestrictedCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <RestrictedCard
            icon={<TrendingUp className="text-[#EE7B11]" />}
            title="Relatório de Inadimplência"
            description="Análises e insights"
            {...getAccessStatus("canViewReports")}
          >
            <Button
              className="w-full bg-[#EE7B11] text-white hover:bg-[#d06a0a]"
              onClick={() => navigate("/inadimplentes")}
            >
              Ver Relatório
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </RestrictedCard>
        </motion.div>
      </div>
    </div>
  );
}

interface InfoCardProps {
  icon: React.ReactElement;
  title: string;
  description: string;
  children: React.ReactNode;
}

function InfoCard({ icon, title, description, children }: InfoCardProps) {
  const iconElement = React.cloneElement(
    icon as React.ReactElement<{ className?: string }>,
    { className: 'w-5 h-5' }
  );

  return (
    <Card className="bg-white border-[#EE7B11]/20 shadow-md hover:shadow-lg transition-shadow duration-300 h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-gray-800 flex items-center gap-2">
          {iconElement}
          <span>{title}</span>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

interface RestrictedCardProps extends InfoCardProps {
  isAccessible: boolean;
  restrictionReason: string;
}

function RestrictedCard({
  icon,
  title,
  description,
  children,
  isAccessible,
  restrictionReason,
}: RestrictedCardProps) {
  const iconElement = React.cloneElement(
    icon as React.ReactElement<{ className?: string }>,
    { className: 'w-5 h-5' }
  );

  return (
    <Card className="bg-white border-[#EE7B11]/20 shadow-md hover:shadow-lg transition-shadow duration-300 relative overflow-hidden h-full">
      {!isAccessible && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm z-10">
          <div className="text-white text-center p-4">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{
                duration: 0.5,
                repeat: Number.POSITIVE_INFINITY,
                repeatType: "reverse",
              }}
            >
              <Lock className="mx-auto mb-2 w-8 h-8" />
            </motion.div>
            <p className="font-semibold">Acesso Restrito</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="link" className="text-white mt-2">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Saiba mais
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{restrictionReason}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}
      <CardHeader className="pb-3">
        <CardTitle className="text-gray-800 flex items-center gap-2">
          {iconElement}
          <span>{title}</span>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

